import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import logo from "../assets/logo1.png";
import "./CSS/Header.css";
import MobileMenu from "./MobileMenu";
import { useI18nField, useL, useLocale } from "../locale/LocaleContext";
import LangToggle from "./LangToggle";

const SubMenu = ({ deptId, subdepartments }) => {
  const ref = useRef(null);
  const [submenuStyle, setSubmenuStyle] = useState({});
  const f = useI18nField();

  useEffect(() => {
    const positionSubmenu = () => {
      if (!ref.current) return;
      const parentRect = ref.current.parentElement.getBoundingClientRect();
      const spaceBelow = window.innerHeight - parentRect.top;
      const estimatedHeight = subdepartments.length * 40;
      const maxHeight = window.innerHeight - 50;

      const style = {
        top: "0",
        bottom: "auto",
        maxHeight: `${maxHeight}px`,
        overflowY: estimatedHeight > maxHeight ? "auto" : "visible",
      };

      if (spaceBelow < estimatedHeight) {
        style.top = "auto";
        style.bottom = "-50px";
      }
      setSubmenuStyle(style);
    };

    positionSubmenu();
    window.addEventListener("resize", positionSubmenu);
    return () => window.removeEventListener("resize", positionSubmenu);
  }, [subdepartments]);

  return (
    <ul
      ref={ref}
      style={submenuStyle}
      className="submenu absolute left-full w-60 bg-white shadow-md rounded hidden group-hover:block z-50"
    >
      {subdepartments.map((sub) => {
        const subName = f(sub, "name");
        return (
          <li key={sub.id}>
            <Link
              to={`/departments/${deptId}/subdepartments/${sub.id}/articles`}
              className="block px-4 py-1 hover:bg-gray-100 truncate w-52"
              title={subName}
            >
              {subName}
            </Link>
          </li>
        );
      })}
    </ul>
  );
};

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentActiveNav, setCurrentActiveNav] = useState(null);
  const [currentActiveParent, setCurrentActiveParent] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [subdepartmentsMap, setSubdepartmentsMap] = useState({});

  const f = useI18nField();
  const t = useL();
  const { lang, setLang } = useLocale();

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "auto";
  }, [isMenuOpen]);

  // Match Tailwind 'lg' (1024px)
  useEffect(() => {
    const DESKTOP_BP = 1024;
    const handleResize = () => {
      if (window.innerWidth >= DESKTOP_BP && isMenuOpen) setIsMenuOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMenuOpen]);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const deptSnap = await getDocs(collection(db, "departments"));
        const deptList = deptSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setDepartments(deptList);

        const subMap = {};
        for (const docSnap of deptSnap.docs) {
          const subSnap = await getDocs(
            collection(db, "departments", docSnap.id, "subdepartments")
          );
          subMap[docSnap.id] = subSnap.docs.map((subdoc) => ({
            id: subdoc.id,
            ...subdoc.data(),
          }));
        }
        setSubdepartmentsMap(subMap);
      } catch (err) {
        console.error("Error loading menu:", err);
      }
    };
    fetchMenu();
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen((v) => !v);
    if (isMenuOpen) getToRoot();
  };

  const getToRoot = () => {
    setCurrentActiveParent("");
    setCurrentActiveNav(null);
  };

  return (
    <header className="sticky top-0 w-full bg-white text-black py-1 z-50">
      <nav className="container mx-auto flex justify-between items-center px-4">
        {/* Left: Logo */}
        <Link to="/" className="logo-link">
          <img src={logo} alt="Logo" style={{ height: "40px", width: "auto" }} />
        </Link>

        {/* Right controls */}
        <div className="flex items-center gap-3">
          {/* Language toggle: md and up */}
          <div className="hidden md:block">
            <LangToggle lang={lang} setLang={setLang} />
          </div>

          {/* Hamburger: visible until lg */}
          <button
            id="nav-container"
            className={`flex lg:hidden ${isMenuOpen ? "pushed" : ""}`}
            onClick={toggleMenu}
            aria-label="Open menu"
            type="button"
          >
            <div className="toggle-icon">
              <span className="bar"></span>
              <span className="bar"></span>
              <span className="bar"></span>
            </div>
          </button>
        </div>

        {/* Desktop nav: lg and up */}
        <ul id="menu" className="hidden lg:flex space-x-4 items-center">
          <li className="relative group">
            <button className="flex items-center">
              {t({ en: "Departments", fr: "Départements" })}
              <i className="fas fa-chevron-down ml-2" style={{ fontSize: "0.75rem" }}></i>
            </button>

            <ul className="absolute left-0 w-72 bg-white shadow-md mt-2 rounded z-50 hidden group-hover:grid grid-cols-2 gap-x-4">
              {departments.map((dept) => {
                const deptName = f(dept, "name");
                return (
                  <li key={dept.id} className="submenu-parent relative group">
                    <Link
                      to={`/departments/${dept.id}`}
                      className="block px-4 py-1 hover:bg-gray-100"
                      title={deptName}
                    >
                      {deptName}
                    </Link>

                    {subdepartmentsMap[dept.id]?.length > 0 && (
                      <SubMenu deptId={dept.id} subdepartments={subdepartmentsMap[dept.id]} />
                    )}
                  </li>
                );
              })}
            </ul>
          </li>

          <li><Link to="/specials">{t({ en: "Promotions", fr: "Promotions" })}</Link></li>
          <li><Link to="/services">{t({ en: "Services", fr: "Services" })}</Link></li>
          <li><Link to="/about-us">{t({ en: "About Us", fr: "À propos" })}</Link></li>
          <li><Link to="/blog">{t({ en: "News", fr: "Actualités" })}</Link></li>
          <li><Link to="/contact">{t({ en: "Contact", fr: "Contact" })}</Link></li>
        </ul>
      </nav>

      <MobileMenu
        isOpen={isMenuOpen}
        toggleMenu={toggleMenu}
        getToRoot={getToRoot}
        currentActiveNav={currentActiveNav}
        currentActiveParent={currentActiveParent}
        setCurrentActiveNav={setCurrentActiveNav}
        setCurrentActiveParent={setCurrentActiveParent}
      />
    </header>
  );
}

export default Header;
